import React from 'react';
import { CodeBlock } from '../CodeBlock';
import { FluidForm } from '../../../shared';
import { SimpleFormProvider } from '../../fluidComponents/fluidForm/SimpleFormLoader';
import { ExampleAuthWrapper } from '../ExampleAuthWrapper';
import { Code2, Box } from 'lucide-react';
export function GettingStartedPage() {
    const formConfigStr = `{
    "key": "getting-started-form",
    "label": "Getting Started",
    "type": "form",
    "fields": [
      { "key": "name", "label": "Name", "type": "fullName" },
      { "key": "email", "label": "Email", "type": "email" },
      { "key": "birthDate", "label": "Birth Date", "type": "date" }
    ]
  }`;
    return (React.createElement(ExampleAuthWrapper, { formConfig: JSON.parse(formConfigStr) },
        React.createElement(GettingStarted, { formConfigStr: formConfigStr })));
}
export function GettingStarted({ formConfigStr }) {
    const formConfig = JSON.parse(formConfigStr);
    const form = new FluidForm(formConfig, {}, { styleConfig: {
            inputModes: ["speech", "camera", "fileUpload", "whatsapp"]
        } });
    return (React.createElement("div", { className: "space-y-5" },
        React.createElement("header", null,
            React.createElement("h1", { className: "text-xl font-semibold text-gray-900" }, "Getting Started"),
            React.createElement("p", { className: "text-gray-600 text-sm mt-1" }, "A quick primer on creating your first FluidForm and rendering it with the built-in provider.")),
        React.createElement("section", { className: "space-y-3" },
            React.createElement("h2", { className: "text-sm font-semibold text-gray-800" }, "Install & Setup"),
            React.createElement(CodeBlock, { language: "bash", code: `# yarn
yarn add fluidform

#npm
npm install fluidform

# cdn
<script src="https://cdn.fluidform.ai/fluidform.js?token=xxx-xxx-xxx-xxx" onload="initFluidForm()"></script>
` })),
        React.createElement("section", { className: "space-y-3" },
            React.createElement("h2", { className: "text-xl font-semibold text-gray-800" }, "Your First Form"),
            React.createElement("div", { className: "grid grid-cols-1 md:grid-cols-2 gap-4 items-start" },
                React.createElement(CodeBlock, { language: "html", code: `
  <FluidForm token="xxx-xxx-xxx-xxx" schema='{
    "label": "Getting Started",
    "type": "form",
    "fields": [
      { "key": "name", "label": "Name", "type": "fullName" },
      { "key": "email", "label": "Email", "type": "email" },
      { "key": "birthDate", "label": "Birth Date", "type": "date" }
    ]
  }'  style="classic" voice camera fileUpload whatsApp />
` }),
                React.createElement("div", { className: "rounded-lg border border-gray-200 bg-white p-3" },
                    React.createElement(SimpleFormProvider, { form: form, submissionId: "getting-started", formProps: {
                            fieldProps: {
                                useStructuredOutput: true,
                                useFieldValidation: true
                            }
                        } })))),
        React.createElement("section", { className: "space-y-4" },
            React.createElement("h2", { className: "text-xl font-semibold text-slate-800" }, "Framework Support"),
            React.createElement("div", { className: "grid grid-cols-1 md:grid-cols-3 gap-4" },
                React.createElement("div", { className: "rounded-lg border border-slate-200 bg-white p-4" },
                    React.createElement("div", { className: "flex items-center gap-2 mb-2" },
                        React.createElement(Code2, { className: "w-5 h-5 text-slate-700" }),
                        React.createElement("div", { className: "font-semibold text-slate-900" }, "React")),
                    React.createElement("p", { className: "text-xs text-slate-600 mb-2" },
                        "Use the built-in ",
                        React.createElement("code", { className: "px-1 bg-slate-100 rounded" }, "SimpleFormProvider"),
                        " to render your ",
                        React.createElement("code", { className: "px-1 bg-slate-100 rounded" }, "FluidForm"),
                        "."),
                    React.createElement(CodeBlock, { language: "bash", code: `yarn add react react-dom fluidform` }),
                    React.createElement(CodeBlock, { language: "tsx", code: `import { FluidForm, SimpleFormProvider } from 'fluidform';

const form = new FluidForm({ key: 'demo', type: 'form', fields: [] }, {});

export default function App() {
  return <FluidForm schema={{
    "label": "Getting Started",
    "type": "form",
    "fields": [
      { "key": "name", "label": "Name", "type": "fullName" },
      { "key": "email", "label": "Email", "type": "email" },
      { "key": "birthDate", "label": "Birth Date", "type": "date" }
    ]
            }}  style="classic" voice camera fileUpload whatsApp urlCapture />
}` })),
                React.createElement("div", { className: "rounded-lg border border-slate-200 bg-white p-4" },
                    React.createElement("div", { className: "flex items-center gap-2 mb-2" },
                        React.createElement(Box, { className: "w-5 h-5 text-slate-700" }),
                        React.createElement("div", { className: "font-semibold text-slate-900" }, "Vue")),
                    React.createElement("p", { className: "text-xs text-slate-600 mb-2" }, "Use the Web Component directly inside your Vue templates and bind a JSON schema."),
                    React.createElement(CodeBlock, { language: "bash", code: `npm create vue@latest my-vue-app -- --ts
cd my-vue-app
yarn add fluidform` }),
                    React.createElement(CodeBlock, { language: "html", code: `<!-- HelloWorld.vue -->
<script setup lang="ts">
const schema = {
  key: 'vue-form',
  type: 'form',
  fields: [
    { key: 'name', label: 'Name', type: 'fullName' },
    { key: 'email', label: 'Email', type: 'email' }
  ]
};
</script>

<template>
  <fluid-form :schema="schema" voice camera fileUpload />
</template>` })),
                React.createElement("div", { className: "rounded-lg border border-slate-200 bg-white p-4" },
                    React.createElement("div", { className: "flex items-center gap-2 mb-2" },
                        React.createElement(Box, { className: "w-5 h-5 text-slate-700" }),
                        React.createElement("div", { className: "font-semibold text-slate-900" }, "Web Components")),
                    React.createElement("p", { className: "text-xs text-slate-600 mb-2" }, "Drop a web component into any HTML page and bind events to sync data."),
                    React.createElement(CodeBlock, { language: "html", code: `<!-- Include your built web component bundle -->
<script type="module" src="/web-components/dist/bundle.js"></script>

<fluid-form
  id="mortgage-form"
  schema='{"key":"mortgage","type":"form","fields":[{"key":"homePrice","type":"number"}]}'>
</fluid-form>

<script>
  const el = document.getElementById('mortgage-form');
  el.addEventListener('change', (e) => {
    console.log('data changed', e.detail);
  });
</script>` }))))));
}
;
export default GettingStarted;
